import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Logo, ArrowRight, Eye, Brain, Calc, Shield, FileText, Sparkles } from '../components/Icons.jsx';

const PASS_KEY = 'tutor-portal-auth-v1';
const PASSWORD = 'medi2026';

const modules = [
  { id: 'venn', to: '/tutor-portal/venn-live', live: true, code: 'DM', icon: Brain, title: 'Venn Reasoning · Live', body: 'Project the scenario, reveal each region on click. Fresh draw every session — no two lessons identical.' },
  { id: 'reading', to: '/tutor-portal/reading-lab-live', live: true, code: 'VR', icon: Eye, title: 'Reading Lab · Live', body: 'Three passages with coaching scripts, target WPM, and reveal-on-click comprehension questions.' },
  { id: 'prob', to: '/tutor-portal/prob-live', live: true, code: 'QR', icon: Calc, title: 'Probability Live', body: 'Step-through probability trees — tap each branch to reveal. Bayes extension + common-mistake reveal.' },
  { id: 'sjt', to: '/tutor-portal/sjt-cards', live: true, code: 'SJT', icon: Shield, title: 'SJT Discussion Cards', body: 'Project a GMC scenario, let students rate silently, then reveal the answer, rationale, and tutor script.' },
  { id: 'syllogism', to: '/tutor-portal/syllogism-board', live: true, code: 'DM', icon: Brain, title: 'Syllogisms Whiteboard', body: 'Reveal premises one at a time. Students commit to a running inference before the conclusion is shown.' },
  { id: 'review', to: '/tutor-portal/mock-review', live: true, code: 'Any', icon: FileText, title: 'Mock Review Walkthrough', body: 'Enter a student\'s section breakdown — get a prioritised coaching agenda with drill prescriptions per weakness.' }
];

export default function TutorPortal() {
  const [authed, setAuthed] = useState(() => sessionStorage.getItem(PASS_KEY) === 'ok');
  const [pw, setPw] = useState('');
  const [err, setErr] = useState('');

  useEffect(() => { document.title = 'Medi · Tutor portal'; }, []);

  const submit = (e) => {
    e.preventDefault();
    if (pw === PASSWORD) {
      sessionStorage.setItem(PASS_KEY, 'ok');
      setAuthed(true);
      setErr('');
    } else {
      setErr('Wrong password. Ask Medic Mind ops for the current passcode.');
    }
  };

  if (!authed) {
    return (
      <div style={{ minHeight: '100vh', display: 'grid', placeItems: 'center', background: 'var(--brand-deep)', padding: 20 }}>
        <form onSubmit={submit} style={{ background: '#fff', borderRadius: 18, padding: '36px 32px', width: '100%', maxWidth: 380, boxShadow: '0 30px 80px rgba(0,0,0,0.3)' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 18 }}>
            <Logo width={36} height={36} onLight />
            <div style={{ display: 'flex', flexDirection: 'column', lineHeight: 1, fontFamily: 'Montserrat, sans-serif' }}>
              <span style={{ fontSize: 20, fontWeight: 800, letterSpacing: '0.04em', textTransform: 'uppercase', color: 'var(--brand-deep)' }}>Medi · Tutor</span>
              <span style={{ fontSize: 9, fontWeight: 600, letterSpacing: '0.18em', textTransform: 'uppercase', color: 'var(--text-muted)', marginTop: 4 }}>powered by Medic Mind</span>
            </div>
          </div>
          <p style={{ color: 'var(--text-muted)', fontSize: 14, marginTop: 0 }}>Live lesson tools for Medic Mind UCAT tutors. Single shared passcode for now.</p>
          <label style={{ fontSize: 13, fontWeight: 600 }}>Passcode</label>
          <input
            type="password" value={pw} onChange={(e) => setPw(e.target.value)} autoFocus
            style={{ width: '100%', padding: '12px 14px', fontSize: 15, border: '1px solid var(--border-strong)', borderRadius: 10, marginTop: 6, marginBottom: 10 }}
          />
          {err && <div style={{ color: '#b91c1c', fontSize: 13, marginBottom: 10 }}>{err}</div>}
          <button type="submit" className="btn btn-primary" style={{ width: '100%' }}>Enter portal <ArrowRight width="16" height="16" /></button>
          <p style={{ color: 'var(--text-muted)', fontSize: 12, marginTop: 16, marginBottom: 0 }}>Students aren't supposed to see this page. Keep the passcode out of public docs.</p>
        </form>
      </div>
    );
  }

  return (
    <div style={{ minHeight: '100vh', background: 'var(--bg)', padding: '24px 32px', maxWidth: 1100, margin: '0 auto' }}>
      <header style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 22, paddingBottom: 16, borderBottom: '1px solid var(--border)' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <Logo width={32} height={32} onLight />
          <div style={{ display: 'flex', flexDirection: 'column', lineHeight: 1, fontFamily: 'Montserrat, sans-serif' }}>
            <span style={{ fontSize: 18, fontWeight: 800, letterSpacing: '0.04em', textTransform: 'uppercase', color: 'var(--brand-deep)' }}>Medi · Tutor portal</span>
            <span style={{ fontSize: 8.5, fontWeight: 600, letterSpacing: '0.18em', textTransform: 'uppercase', color: 'var(--text-muted)', marginTop: 4 }}>powered by Medic Mind</span>
          </div>
        </div>
        <button
          onClick={() => { sessionStorage.removeItem(PASS_KEY); setAuthed(false); setPw(''); }}
          style={{ background: 'none', border: '1px solid var(--border)', borderRadius: 8, padding: '8px 12px', fontSize: 13, cursor: 'pointer' }}
        >Sign out</button>
      </header>

      <div className="page-head" style={{ marginBottom: 18 }}>
        <h1 style={{ fontSize: 30 }}>Pick a live lesson tool</h1>
        <p className="subtitle">Each module is built for Zoom/Teams screen-share — big fonts, hidden answers, reveal-on-click, and a tutor-only side panel that students never see. Every session pulls a fresh draw so no two lessons are identical.</p>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))', gap: 16 }}>
        {modules.map((m) => (
          <ModCard key={m.id} m={m} />
        ))}
      </div>

      <section className="card" style={{ marginTop: 28, background: 'var(--card-soft)' }}>
        <h3 style={{ margin: '0 0 6px', fontSize: 17 }}>Why this isn't slides</h3>
        <ul style={{ margin: 0, paddingLeft: 18, color: 'var(--text-2)', fontSize: 14, lineHeight: 1.7 }}>
          <li><strong>Randomised content</strong> — same module, different numbers / scenarios every session. Tutors can't run on autopilot.</li>
          <li><strong>Hidden answers</strong> — the answer doesn't render until you tap Reveal. Forces the student to actually attempt it first.</li>
          <li><strong>Tutor-only panel</strong> — common mistakes, 30-sec script, and follow-up prompts visible only on your screen.</li>
          <li><strong>Student companion link</strong> — one click copies a URL you paste into Zoom/Teams chat. Student gets the same drill, their answer comes back to your screen.</li>
        </ul>
      </section>
    </div>
  );
}

function ModCard({ m }) {
  const Inner = (
    <>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 10 }}>
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8, color: 'var(--accent)', fontSize: 12, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em' }}>
          <m.icon width="16" height="16" />{m.code}
        </span>
        <span style={{
          fontSize: 11, padding: '3px 10px', borderRadius: 99, fontWeight: 700,
          background: m.live ? '#e7f7ef' : '#fef3c7',
          color: m.live ? 'var(--live-green)' : '#b45309',
          border: `1px solid ${m.live ? 'rgba(16, 185, 129, 0.35)' : '#fde68a'}`
        }}>{m.live ? 'Live' : 'Coming soon'}</span>
      </div>
      <h3 style={{ margin: '0 0 8px', fontSize: 18, color: 'var(--text)' }}>{m.title}</h3>
      <p style={{ margin: 0, color: 'var(--text-2)', fontSize: 14, lineHeight: 1.55 }}>{m.body}</p>
      <div style={{ marginTop: 14, color: m.live ? 'var(--accent)' : 'var(--text-muted)', fontSize: 14, fontWeight: 600, display: 'inline-flex', alignItems: 'center', gap: 6 }}>
        {m.live ? <>Open module <ArrowRight width="14" height="14" /></> : 'In design'}
      </div>
    </>
  );
  return m.live
    ? <Link to={m.to} className="drill-card" style={{ textDecoration: 'none' }}>{Inner}</Link>
    : <div className="drill-card" style={{ opacity: 0.7 }}>{Inner}</div>;
}
